import { Link } from "react-router-dom";
import { ArrowRight, Phone, Smartphone } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import PlanCard from "@/components/PlanCard";
import ExtraInternetTable from "@/components/ExtraInternetTable";
import PolandAnimatedMap from "@/components/PolandAnimatedMap";
import Countdown from "@/components/Countdown";
import PagePlacements from "@/components/PagePlacements";

export default function Home() {
  const { phone_plans, brand } = useContent();
  const eyebrowStyle = brand?.hero_eyebrow_style || { size: "md", flag_speed_sec: 12 };
  // Mapowanie rozmiaru na klasy Tailwind
  const sizeClasses = {
    sm: "text-lg sm:text-xl lg:text-2xl",
    md: "text-2xl sm:text-3xl lg:text-4xl",
    lg: "text-3xl sm:text-4xl lg:text-5xl",
    xl: "text-4xl sm:text-5xl lg:text-6xl",
  }[eyebrowStyle.size] || "text-2xl sm:text-3xl lg:text-4xl";
  const speed = Math.max(1, Math.min(120, Number(eyebrowStyle.flag_speed_sec) || 12));

  // === Formatowanie nagłówka „hero title" (Republika mobile łączymy pokolenia) ===
  const titleStyle = brand?.hero_title_style || {};
  const HERO_FONT_STACKS = {
    manrope: "'Manrope', system-ui, sans-serif",
    inter: "'Inter', system-ui, sans-serif",
    roboto: "'Roboto', system-ui, sans-serif",
    lato: "'Lato', system-ui, sans-serif",
    poppins: "'Poppins', system-ui, sans-serif",
    playfair: "'Playfair Display', Georgia, serif",
    merriweather: "'Merriweather', Georgia, serif",
    bebas: "'Bebas Neue', Impact, sans-serif",
    oswald: "'Oswald', Impact, sans-serif",
  };
  const HERO_SIZE_CLASSES = {
    xs: "text-lg sm:text-xl lg:text-2xl",
    sm: "text-xl sm:text-2xl lg:text-3xl",
    md: "text-2xl sm:text-3xl lg:text-4xl",
    lg: "text-3xl sm:text-4xl lg:text-5xl",
    xl: "text-4xl sm:text-5xl lg:text-6xl",
    "2xl": "text-4xl sm:text-5xl lg:text-6xl",
    "3xl": "text-4xl sm:text-5xl lg:text-6xl",
    "4xl": "text-5xl sm:text-6xl lg:text-7xl",
    "5xl": "text-6xl sm:text-7xl lg:text-8xl",
  };
  const heroFontFamily = HERO_FONT_STACKS[titleStyle.font_family] || HERO_FONT_STACKS.manrope;
  const heroSizeCls = HERO_SIZE_CLASSES[titleStyle.size] || HERO_SIZE_CLASSES["3xl"];
  const heroWeight = Number(titleStyle.weight) || 900;
  const heroItalic = titleStyle.italic ? "italic" : "";

  return (
    <div data-testid="home-page" className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 rm-grid-noise opacity-30 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-6 pb-12 md:pt-10 md:pb-16 relative">
          {/* Eyebrow – pełna szerokość, ponad gridem */}
          <div
            data-testid="hero-eyebrow"
            className={`rm-half-text font-display font-black uppercase tracking-tight leading-none mb-4 md:mb-6 ${sizeClasses}`}
            style={{ animationDuration: `${speed}s` }}
          >
            {brand.hero.eyebrow}
          </div>

          {/* Zegar odliczający do startu sprzedaży — na wierzchu, wyśrodkowany */}
          <Countdown
            label={brand?.countdown?.label}
            targetAt={brand?.countdown?.target_at}
            enabled={brand?.countdown?.enabled !== false}
          />

          <div className="grid md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-7 rm-rise">
              <h1
                data-testid="hero-title"
                className={`font-display text-white leading-[1.05] whitespace-pre-line ${heroSizeCls} ${heroItalic}`}
                style={{
                  fontFamily: heroFontFamily,
                  fontWeight: heroWeight,
                }}
              >
                {brand.hero.title}
              </h1>
              <p className="text-base md:text-lg text-white/80 mt-6 max-w-xl leading-relaxed">
                {brand.hero.subtitle}
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/abonamenty" data-testid="hero-cta-plans" className="rm-btn-primary inline-flex items-center gap-2">
                  Zobacz abonamenty <ArrowRight size={18} />
                </Link>
                {brand.hero?.cta_port_enabled !== false && (
                  <Link to="/przenies-numer" data-testid="hero-cta-port" className="rm-btn-outline inline-block">
                    Przenieś numer
                  </Link>
                )}
              </div>

              <div className="mt-12 grid grid-cols-3 gap-6 max-w-md hidden">
                {[].map((s, i) => (
                  <div key={i} data-testid={`hero-stat-${i}`}>
                    <div className="font-display text-3xl font-black text-white">{s.v}</div>
                    <div className="text-xs text-white/60 uppercase tracking-wider mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="md:col-span-5 rm-rise rm-rise-delay-2 relative min-h-[420px] md:min-h-[480px] flex items-center justify-center">
              {/* Animowana mapa Polski — sekwencyjne rysowanie łuków 5G. */}
              <PolandAnimatedMap className="w-full max-w-[520px]" />
            </div>
          </div>
        </div>
      </section>

      {/* PLANS */}
      <section id="abonamenty" className="section-pad">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="text-white/70 text-xs uppercase tracking-widest font-semibold">{brand.plans_heading?.eyebrow || "Cennik"}</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black text-white mt-3">
              {brand.plans_heading?.title || "Wybierz swój abonament"}
            </h2>
            <p className="text-white/70 mt-4">
              {brand.plans_heading?.subtitle || "Dwa proste plany. Bez ukrytych opłat, bez gwiazdek w umowie."}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-6 max-w-4xl">
            {phone_plans.map((p) => (
              <PlanCard key={p.id} plan={p} />
            ))}
          </div>
        </div>
      </section>

      {/* EXTRA INTERNET CODES */}
      <ExtraInternetTable />

      {/* FEATURES */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-6">
            {(brand.features && brand.features.length > 0
              ? brand.features
              : [
                  { title: "Karta SIM lub eSIM", desc: "Wybierz tradycyjną kartę SIM lub aktywuj eSIM w kilka minut bez wychodzenia z domu." },
                  { title: "Przenieś swój numer", desc: "Zachowaj dotychczasowy numer telefonu. Cały proces przeprowadzimy za Ciebie." },
                ]
            ).map((f, i) => (
              <div key={i} className="rm-card-dark p-7">
                <div className="w-12 h-12 rounded-xl bg-[#dc2626]/20 text-[#ef4444] flex items-center justify-center mb-5">
                  {i % 2 === 0 ? <Smartphone size={22} /> : <Phone size={22} />}
                </div>
                <h3 className="font-display text-xl font-bold text-white">{f.title}</h3>
                <p className="text-white/70 text-sm mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="rm-card p-10 md:p-14 text-center">
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#0f172a]">
              {brand.cta_home?.title || "Gotowy, by zmienić sieć?"}
            </h2>
            <p className="text-slate-600 mt-3 max-w-xl mx-auto">
              {brand.cta_home?.subtitle || "Zamów kartę SIM z dostawą do domu lub przenieś swój dotychczasowy numer do Rmobile."}
            </p>
            <div className="flex flex-wrap gap-3 justify-center mt-6">
              {(() => {
                const btn = brand.cta_home?.primary_btn;
                if (!btn || btn.enabled === false) return null;
                const url = btn.url || "/zamow-sim";
                const isExternal = /^https?:\/\//i.test(url);
                const label = btn.text || "Zamów kartę SIM";
                if (isExternal) {
                  return (
                    <a href={url} target="_blank" rel="noopener noreferrer" data-testid="bottom-cta-order" className="rm-btn-primary">
                      {label}
                    </a>
                  );
                }
                return (
                  <Link to={url} data-testid="bottom-cta-order" className="rm-btn-primary">
                    {label}
                  </Link>
                );
              })()}
              {(() => {
                const btn = brand.cta_home?.secondary_btn;
                if (!btn || btn.enabled === false) return null;
                const url = btn.url || "/przenies-numer";
                const isExternal = /^https?:\/\//i.test(url);
                const label = btn.text || "Przenieś numer";
                const cls = "px-6 py-3 rounded-full border border-[#1f3a8a] text-[#1f3a8a] font-bold hover:bg-[#1f3a8a] hover:text-white transition";
                if (isExternal) {
                  return (
                    <a href={url} target="_blank" rel="noopener noreferrer" data-testid="bottom-cta-port" className={cls}>
                      {label}
                    </a>
                  );
                }
                return (
                  <Link to={url} data-testid="bottom-cta-port" className={cls}>
                    {label}
                  </Link>
                );
              })()}
            </div>
          </div>
        </div>
      </section>
      <PagePlacements page="home" />
    </div>
  );
}
